import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { LabeledCount, formatMttrMinutes } from '../../../../../../../api/teamMetricsApi';
import { DetectionMethodPair, TimeDistribution } from './aggregateIncidents';
import { TimeDistributionDotPlot } from './TimeDistributionDotPlot';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  card: {
    padding: theme.spacing(3),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  infoIcon: {
    fontSize: 14,
    color: '#CBD5E0',
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
  mainValue: {
    fontSize: 36,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  subtitle: {
    fontSize: 10,
    color: '#8E9299',
    marginTop: theme.spacing(0.5),
  },
  severityBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
  },
  severityRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  severityLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  severityDot: {
    width: 8,
    height: 8,
    borderRadius: 2,
  },
  severityText: {
    fontSize: 11,
    color: '#4A5568',
    fontWeight: 500,
  },
  severityValue: {
    fontSize: 11,
    color: '#718096',
    fontWeight: 600,
  },
  detectionGap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  gapWarning: {
    color: '#DD6B20',
  },
  gapOk: {
    color: '#38A169',
  },
  mttrList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  detectionBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1.5),
  },
  detectionPair: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 10,
    padding: theme.spacing(0.5, 0),
  },
  detectionPairText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,
  },
  detectionArrow: {
    color: '#A0AEC0',
    fontSize: 10,
    flexShrink: 0,
  },
  detectionMethod: {
    fontSize: 10,
    color: '#4A5568',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 70,
  },
  detectionCount: {
    fontSize: 10,
    color: '#718096',
    fontWeight: 600,
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  breakdownSection: {
    marginTop: theme.spacing(1),
  },
  breakdownLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: '#A0AEC0',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: theme.spacing(0.5),
  },
}));

const SEVERITY_COLORS: Record<string, string> = {
  'SEV 1': '#F56565',
  'SEV 2': '#ED8936',
  'SEV 3': '#ECC94B',
  'SEV 4': '#9F7AEA',
  'TBD': '#A0AEC0',
};

const SEVERITY_ORDER = ['SEV 1', 'SEV 2', 'SEV 3', 'SEV 4', 'TBD'];

interface IncidentsSummaryProps {
  totalIncidents: number;
  orgTotalIncidents?: number;
  severityDistribution: LabeledCount[];
  orgSeverityDistribution?: LabeledCount[];
  ttdMinutes: number | null;
  ttdDistribution: TimeDistribution;
  ttrMinutes: number | null;
  ttrDistribution: TimeDistribution;
  detectionMismatchCount: number;
  detectionMismatchTotal: number;
  detectionMismatches: DetectionMethodPair[];
  detectionMatches: DetectionMethodPair[];
  mttrBySeverity: Record<string, number | null>;
}

export const IncidentsSummary: React.FC<IncidentsSummaryProps> = ({
  totalIncidents,
  orgTotalIncidents,
  severityDistribution,
  orgSeverityDistribution,
  ttdMinutes,
  ttdDistribution,
  ttrMinutes,
  ttrDistribution,
  detectionMismatchCount,
  detectionMismatchTotal,
  detectionMismatches,
  detectionMatches,
  mttrBySeverity,
}) => {
  const classes = useStyles();

  const percentage = orgTotalIncidents && orgTotalIncidents > 0
    ? Math.round((totalIncidents / orgTotalIncidents) * 100)
    : null;

  const orgSeverityMap = new Map<string, number>();
  if (orgSeverityDistribution) {
    orgSeverityDistribution.forEach((s) => orgSeverityMap.set(s.label, s.count));
  }

  const severityWithOrg = SEVERITY_ORDER.map((label) => {
    const filtered = severityDistribution.find((s) => s.label === label)?.count ?? 0;
    const org = orgSeverityMap.get(label) ?? 0;
    return { label, filtered, org };
  }).filter((s) => s.filtered > 0 || s.org > 0);

  const detectionGapPercent = detectionMismatchTotal > 0
    ? Math.round((detectionMismatchCount / detectionMismatchTotal) * 100)
    : null;

  const formatMinutes = (minutes: number | null): string => {
    if (minutes === null) return 'N/A';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <Box className={classes.container}>
      {/* Total Incidents Card */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Total Incidents</Typography>
          <Tooltip title="Total number of incidents matching current filters out of all incidents in the selected time period">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
        <Box className={classes.valueRow}>
          <Typography className={classes.mainValue}>
            {orgTotalIncidents !== undefined ? `${totalIncidents} / ${orgTotalIncidents}` : totalIncidents}
          </Typography>
          {percentage !== null && (
            <Typography sx={{ fontSize: 14, color: '#718096', fontWeight: 500, marginLeft: 1 }}>
              ({percentage}%)
            </Typography>
          )}
        </Box>
        {severityWithOrg.length > 0 && (
          <Box className={classes.severityBreakdown}>
            {severityWithOrg.map((sev) => {
              const sevPercent = sev.org > 0 ? Math.round((sev.filtered / sev.org) * 100) : null;
              return (
                <Box key={sev.label} className={classes.severityRow}>
                  <Box className={classes.severityLabel}>
                    <Box
                      className={classes.severityDot}
                      sx={{ backgroundColor: SEVERITY_COLORS[sev.label] || '#A0AEC0' }}
                    />
                    <Typography className={classes.severityText}>{sev.label}</Typography>
                  </Box>
                  <Typography className={classes.severityValue}>
                    {sev.filtered} / {sev.org}
                    {sevPercent !== null && (
                      <span style={{ color: '#A0AEC0', marginLeft: 4 }}>({sevPercent}%)</span>
                    )}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Time to Detection Card */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Avg TTD</Typography>
          <Tooltip title="Average Time To Detection is calculated from 'Start Timestamp' to 'Detection Timestamp' fields in the Incident Tracker. Persistently high TTD indicates insufficient alerting coverage for a product area. This is an early warning signal that monitoring investments are needed.">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
        <Box className={classes.valueRow}>
          <Typography className={classes.mainValue}>{formatMinutes(ttdMinutes)}</Typography>
        </Box>
        <TimeDistributionDotPlot
          dataPoints={ttdDistribution.dataPoints}
          median={ttdDistribution.median}
          mean={ttdDistribution.mean}
          notRecordedCount={ttdDistribution.notRecorded.count}
          metricLabel="TTD"
        />
      </Paper>

      {/* Detection Method Gap Card */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Detection Gap</Typography>
          <Tooltip title="Incidents detected via 'Customer Reported' or 'Internal Report' represent gaps in proactive monitoring. Other methods (Alert, Monitoring Deployment, External Notification, Vulnerability Scanning) indicate we detected the issue before customers were impacted. A high gap % suggests investing in better alerting and observability.">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
        <Box className={classes.valueRow}>
          <Typography className={classes.mainValue}>
            {detectionGapPercent !== null ? `${detectionMismatchCount}/${detectionMismatchTotal} (${detectionGapPercent}%)` : 'N/A'}
          </Typography>
        </Box>
        {(detectionMismatches.length > 0 || detectionMatches.length > 0) && (
          <Box className={classes.detectionBreakdown}>
            {detectionMismatches.length > 0 && (
              <Box className={classes.breakdownSection}>
                <Typography className={classes.breakdownLabel}>Gaps</Typography>
                {detectionMismatches.map((pair, idx) => (
                  <Box key={idx} className={classes.detectionPair}>
                    <Box className={classes.detectionPairText}>
                      <WarningAmberIcon sx={{ fontSize: 10, color: '#DD6B20', mr: 0.5 }} />
                      <Typography className={classes.detectionMethod} sx={{ maxWidth: 'none' }}>
                        {pair.actual}
                      </Typography>
                    </Box>
                    <Typography className={classes.detectionCount}>({pair.count})</Typography>
                  </Box>
                ))}
              </Box>
            )}
            {detectionMatches.length > 0 && (
              <Box className={classes.breakdownSection}>
                <Typography className={classes.breakdownLabel}>Proactive</Typography>
                {detectionMatches.map((pair, idx) => (
                  <Box key={idx} className={classes.detectionPair}>
                    <Box className={classes.detectionPairText}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 10, color: '#38A169', mr: 0.5 }} />
                      <Typography className={classes.detectionMethod} sx={{ maxWidth: 'none' }}>
                        {pair.actual}
                      </Typography>
                    </Box>
                    <Typography className={classes.detectionCount}>({pair.count})</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Time to Response Card */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>Avg TTR</Typography>
          <Tooltip title="Average Time To Response is calculated from 'Detection Timestamp' to 'Response Initiated' fields. Measures whether on-call rotations and escalation paths are functioning. Process SLAs: under 5 minutes during business hours, under 30 minutes off-hours. Both are measurable from existing timestamp data.">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
        <Box className={classes.valueRow}>
          <Typography className={classes.mainValue}>{formatMinutes(ttrMinutes)}</Typography>
        </Box>
        <TimeDistributionDotPlot
          dataPoints={ttrDistribution.dataPoints}
          median={ttrDistribution.median}
          mean={ttrDistribution.mean}
          notRecordedCount={ttrDistribution.notRecorded.count}
          metricLabel="TTR"
        />
      </Paper>

      {/* MTTR by Severity Card */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.labelRow}>
          <Typography className={classes.label}>MTTR by Severity</Typography>
          <Tooltip title="Mean Time to Resolution (Incident Start to Recovery) broken down by severity. Raw MTTR is misleading without segmentation - a single long SEV-2 can inflate monthly figures and create false performance signals. SEV-1 and SEV-2 are the primary focus for operational health.">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
        <Box className={classes.mttrList}>
          {SEVERITY_ORDER.slice(0, 4).map((sev) => {
            const mttr = mttrBySeverity[sev];
            return (
              <Box key={sev} className={classes.severityRow}>
                <Box className={classes.severityLabel}>
                  <Box
                    className={classes.severityDot}
                    sx={{ backgroundColor: SEVERITY_COLORS[sev] }}
                  />
                  <Typography className={classes.severityText}>{sev}</Typography>
                </Box>
                <Typography className={classes.severityValue}>
                  {formatMttrMinutes(mttr)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default IncidentsSummary;
